
import { Component, Vue, Prop, Watch } from 'vue-property-decorator';
import { google } from 'google-maps';
import { usePathStore } from '../stores/pathsStore';
import { SimplePath } from '../models/SimplePath';
import { SimplePoint } from '../models/SimplePoint';
import { Point } from '../models/Point';
import { GpsHelper } from '../services/GpsHelper';
import RenderColors from '../services/colors';
import { RenderHelper } from '../services/RenderHelper';

@Component
export default class FlightpathRenderPath extends Vue {
  @Prop() googleApi!: google;
  @Prop() map!: google.maps.Map<HTMLDivElement>;

  public pathStore = usePathStore();
  //We recenter when we receive a new path
  lastPathId = null as string | null;
  line!: google.maps.Polyline;
  polygon!: google.maps.Polygon;
  workArea: google.maps.Polygon | null = null;
  lineListenerHandle: google.maps.MapsEventListener | null = null;
  lineDragListenerHandle: google.maps.MapsEventListener | null = null;
  mapClickListenerHandle: google.maps.MapsEventListener | null = null;
  polygonClickListenerHandle: google.maps.MapsEventListener | null = null;
  workAreaClickListenerHandle: google.maps.MapsEventListener | null = null;
  workAreaDragListenerHandle: google.maps.MapsEventListener | null = null;
  startMarker: google.maps.Marker | null = null;
  endMarker: google.maps.Marker | null = null;

  async mounted() {
    this.addMapClickListener();
    this.createNewPolygon([]);
  }

  destroyed() {
    this.destroyResources();
  }

  deactivated() {
    this.destroyResources();
  }

  private destroyResources() {
    this.lineListenerHandle?.remove();
    this.lineDragListenerHandle?.remove();
    this.mapClickListenerHandle?.remove();
    this.polygonClickListenerHandle?.remove();
    this.workAreaClickListenerHandle?.remove();
    this.workAreaDragListenerHandle?.remove();
    this.line?.setMap(null);
    this.polygon?.setMap(null);
    this.workArea?.setMap(null);
    this.startMarker?.setMap(null);
    this.endMarker?.setMap(null);
  }

  @Watch('pathStore.selectedPath')
  onSelectedPathChange(path: SimplePath): void {
    this.renderPath(path);
  }

  @Watch('pathStore.editable')
  onEditableChange(): void {
    if (this.line) {
      this.line.setEditable(this.pathStore.edit);
      this.updateLineMarkers();
    }
  }

  @Watch('pathStore.editWorkArea')
  onEditWorkAreaChange(value: boolean): void {
    if (this.line) {
      this.line.setEditable(!value);
    }

    if (this.workArea) {
      this.workArea.setEditable(value);
      this.workArea.setOptions({
        strokeColor: value ? RenderColors.WorkArea : 'transparent'
      });
    }
  }

  @Watch('pathStore.pathAssociatedField')
  onPathAssociatedFieldChange(): void {
    this.polygon.setMap(null);
    this.createNewPolygon(this.pathStore.associatedField.BoundaryPoints);
  }

  @Watch('pathStore.clearMap')
  onClearMapChange(): void {
    this.line?.setMap(null);
    this.polygon?.setMap(null);
    this.workArea?.setMap(null);
    this.startMarker?.setMap(null);
    this.endMarker?.setMap(null);
  }

  @Watch('pathStore.addPath')
  onAddPathChange(value: boolean): void {
    if (value) {
      if (this.line) {
        this.line.setPath([]);
        this.line.setMap(this.map);
        this.pathStore.updateCurrentLine(
          this.line
            .getPath()
            .getArray()
            .map((o) => [o.lng(), o.lat()])
        );
      }
    }
  }

  @Watch('pathStore.revertPath')
  onRevertPathChange(): void {
    this.line.setMap(null);
    this.createNewPolyline(this.pathStore.selectedPath?.Path as SimplePoint[]);
    this.pathStore.setSelectedPathLength(this.findPathLengthInMeters(this.line));
    this.pathStore.updateCurrentLine(
      this.line
        .getPath()
        .getArray()
        .map((o) => [o.lng(), o.lat()])
    );
  }

  @Watch('pathStore.revertWorkArea')
  onRevertWorkAreaChange(): void {
    if (!this.workArea) return;
    this.workArea.setMap(null);
    this.createNewWorkAreaPolygon(this.pathStore.currentWorkArea.map((value) => ({ Lat: value[1], Long: value[0], Alt: 0 })));
  }

  @Watch('pathStore.deleteWorkArea')
  onDeleteWorkAreaChange() {
    if (!this.workArea) return;
    this.workArea.setMap(null);
    this.createNewWorkAreaPolygon([]);
  }

  createNewPolyline(path: SimplePoint[]) {
    this.line = new this.googleApi.maps.Polyline(RenderHelper.getPathPolylineOptionsFromSimplePoint(path));
    this.line.setOptions({ zIndex: 100 });
    this.line.setMap(this.map);
    this.line.setEditable(this.pathStore.editable);

    this.addLineClickListener();
    this.addLineDragListener();
  }

  createNewPolygon(boundary: Point[]) {
    this.polygon = new this.googleApi.maps.Polygon(RenderHelper.getFieldPolygonOptionsFromPoint(boundary));
    this.polygon.setOptions({ zIndex: 0 });
    this.polygon.setMap(this.map);

    this.addPolygonClickListener();
  }

  createNewWorkAreaPolygon(boundary: SimplePoint[]) {
    if (this.workArea) this.workArea.setMap(null);
    this.workArea = new this.googleApi.maps.Polygon(RenderHelper.getWorkAreaPolygonOptionsFromSimplePoint(boundary));
    this.workArea.setMap(this.map);

    this.updateWorkArea();
    this.addWorkAreaClickListener();
    this.addWorkAreaDragListener();
  }

  addLineClickListener() {
    if (!this.line) return;

    if (this.lineListenerHandle != null) {
      this.lineListenerHandle.remove();
    }

    this.lineListenerHandle = this.line.addListener('click', (e) => {
      if (!this.pathStore.editable) return;

      const newPath = this.line.getPath();

      if (newPath.getLength() == 1) return;

      if (e.vertex != undefined) {
        newPath.removeAt(e.vertex);
        this.line.setPath(newPath);
        this.updateLineMarkers();
        this.pathStore.setSelectedPathLength(this.findPathLengthInMeters(this.line));
        this.pathStore.updateCurrentLine(
          this.line
            .getPath()
            .getArray()
            .map((o) => [o.lng(), o.lat()])
        );
      }
    });
  }

  addLineDragListener() {
    if (!this.line) return;

    if (this.lineDragListenerHandle != null) {
      this.lineDragListenerHandle.remove();
    }

    this.lineDragListenerHandle = this.line.addListener('mouseup', () => {
      this.updateLineMarkers();
      this.pathStore.setSelectedPathLength(this.findPathLengthInMeters(this.line));
      this.pathStore.updateCurrentLine(
        this.line
          .getPath()
          .getArray()
          .map((o) => [o.lng(), o.lat()])
      );
    });
  }

  addMapClickListener() {
    if (this.mapClickListenerHandle != null) {
      this.mapClickListenerHandle.remove();
    }

    this.mapClickListenerHandle = this.map.addListener('click', (e) => {
      this.mapClickHandler(e);
    });
  }

  addPolygonClickListener() {
    if (this.polygonClickListenerHandle != null) {
      this.polygonClickListenerHandle.remove();
    }

    this.polygonClickListenerHandle = this.polygon.addListener('click', (e) => {
      this.mapClickHandler(e);
    });
  }

  addWorkAreaClickListener() {
    if (this.workAreaClickListenerHandle != null) {
      this.workAreaClickListenerHandle.remove();
    }

    if (this.workArea) {
      this.workAreaClickListenerHandle = this.workArea.addListener('click', (e) => {
        if (e.vertex != undefined && this.pathStore.editWorkArea) {
          this.deletePointFromPath(e.vertex);
        } else {
          this.mapClickHandler(e);
        }
      });
    }
  }

  addWorkAreaDragListener() {
    if (!this.workArea) return;

    if (this.workAreaDragListenerHandle != null) {
      this.workAreaDragListenerHandle.remove();
    }

    this.workAreaDragListenerHandle = this.workArea.addListener('mouseup', () => {
      this.updateWorkArea();
    });
  }

  mapClickHandler(e: google.maps.MouseEvent) {
    if (!this.pathStore.editable) return;

    if (this.pathStore.editWorkArea) {
      this.updateWorkAreaPolygon(e);
    } else {
      this.updateLine(e);
    }
  }

  centerMap(path: SimplePath) {
    let points: google.maps.LatLng[] = [];
    const field = this.pathStore.associatedField;

    if (field.BoundaryPoints.length > 0) {
      points = field.BoundaryPoints.map((point) => new this.googleApi.maps.LatLng(point.Item1, point.Item2));
    } else if (path.WorkAreas.length > 0 && path.WorkAreas[0].length > 0) {
      points = path.WorkAreas[0].map((point) => new this.googleApi.maps.LatLng(point.Lat, point.Long));
    } else if (path.Path.length > 0) {
      points = path.Path.map((point) => new this.googleApi.maps.LatLng(point.Lat, point.Long));
    }

    if (points.length > 0 && path.PathID !== this.lastPathId) {
      const bounds = new google.maps.LatLngBounds();
      points.forEach((point) => bounds.extend(point));
      this.map.fitBounds(bounds);
      this.lastPathId = path.PathID;
    }
  }

  renderPath(path: SimplePath) {
    if (path.WorkAreas != undefined && path.WorkAreas.length > 0) {
      const array = path.WorkAreas[0].map((value: SimplePoint) => new this.googleApi.maps.LatLng(value.Lat, value.Long));
      this.pathStore.setCurrentWorkArea(array.map((o) => [o.lng(), o.lat()]));
      const area = this.googleApi.maps.geometry.spherical.computeArea(array);
      this.pathStore.setSelectedWorkAreaArea(Math.round(area));
      this.createNewWorkAreaPolygon(path.WorkAreas[0]);
    } else {
      this.createNewWorkAreaPolygon([]);
    }

    if (this.line) this.line.setMap(null);
    if (!path) return;
    this.createNewPolyline(path.Path);

    this.updateLineMarkers();
    this.pathStore.setSelectedPathLength(this.findPathLengthInMeters(this.line));
    this.pathStore.updateCurrentLine(
      this.line
        .getPath()
        .getArray()
        .map((o) => [o.lng(), o.lat()])
    );
  }

  updateLineMarkers() {
    if (this.startMarker) this.startMarker.setMap(null);
    this.startMarker = RenderHelper.createStartMakerWithLabel(this.line, this.pathStore.editable);
    this.startMarker.setMap(this.map);

    if (this.endMarker) this.endMarker.setMap(null);
    this.endMarker = RenderHelper.createStopMakerWithLabel(this.line, this.pathStore.editable);
    this.endMarker.setMap(this.map);
  }

  updateLine(e: google.maps.MouseEvent) {
    if (!this.line) {
      this.createNewPolyline([]);
    }

    const newPath = this.line.getPath();
    if (newPath.getLength() == 0) {
      newPath.push(e.latLng);
    } else {
      const startPointCloser = this.isStartPointCloser(newPath.getAt(0), newPath.getAt(newPath.getLength() - 1), e.latLng);
      if (startPointCloser) {
        newPath.insertAt(0, e.latLng);
      } else {
        newPath.push(e.latLng);
      }
    }

    this.line.setPath(newPath);
    this.updateLineMarkers();
    this.pathStore.setSelectedPathLength(this.findPathLengthInMeters(this.line));
    this.pathStore.updateCurrentLine(
      this.line
        .getPath()
        .getArray()
        .map((o) => [o.lng(), o.lat()])
    );
  }

  deletePointFromPath(vertexClicked: number) {
    if (!this.workArea) return;
    const newPath = this.workArea.getPath();
    if (newPath.getLength() <= 3) return;

    newPath.removeAt(vertexClicked);
    this.workArea.setPath(newPath);
    this.updateWorkArea();
  }

  updateWorkAreaPolygon(e: google.maps.MouseEvent) {
    if (!this.workArea) {
      this.createNewWorkAreaPolygon([]);
    }
    this.addPointToWorkArea(e);
  }

  addPointToWorkArea(e: google.maps.MouseEvent) {
    if (!this.workArea) return;
    const newPath = this.workArea.getPath();
    if (newPath == undefined || newPath.getLength() == 0) {
      this.workArea.setPath([e.latLng]);
    } else {
      newPath.push(e.latLng);
      this.workArea.setPath(newPath);
    }

    this.updateWorkArea();
  }

  updateWorkArea() {
    if (!this.workArea) return;
    let area = 0;
    let currentWorkArea: google.maps.LatLng[] = [];

    if (this.workArea.getPath() != undefined) {
      currentWorkArea = this.workArea.getPath().getArray();
      area = this.googleApi.maps.geometry.spherical.computeArea(this.workArea.getPath());
    }

    this.pathStore.setCurrentWorkArea(currentWorkArea.map((o) => [o.lng(), o.lat()]));
    this.pathStore.setSelectedWorkAreaArea(Math.round(area));
  }

  /**
   * Checks if the start point or end point is closest to the clicked point.
   * Returns true if the start point is closest to the clicked point.
   */
  isStartPointCloser(startPoint: google.maps.LatLng, endPoint: google.maps.LatLng, clickedPoint: google.maps.LatLng): boolean {
    const distanceToStart = Math.hypot(startPoint.lat() - clickedPoint.lat(), startPoint.lng() - clickedPoint.lng());
    const distanceToEnd = Math.hypot(endPoint.lat() - clickedPoint.lat(), endPoint.lng() - clickedPoint.lng());
    return distanceToStart < distanceToEnd;
  }

  /**
   * Returns the length of the path in meters
   */
  findPathLengthInMeters(path: google.maps.Polyline) {
    const points = path.getPath().getArray();
    const distance = GpsHelper.findPathLengthInMeters(points);
    return Math.round(distance);
  }
}
